import { useAuth0 } from "@auth0/auth0-react";
import { DISMISS, useNotification } from "collections";
import { Form } from "collections/Form";
import { ModalForm, useModalDispatch } from "collections/Modal";
import {
  CancelButton,
  FileUploadReader,
  FileUploadResponse,
  SubmitButton
} from "elements";
import { useAddResourceFileMutation } from "generated/graphql";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useContext, useState } from "react";
import i18n, { useTranslation } from "utilities/i18n";
import * as yup from "yup";

const MAX_SIZE_IN_MB = 3.5;
const MAX_SIZE = MAX_SIZE_IN_MB * 1024 * 1024; // in bytes

const AddResourceFileFormSchema = yup.object({
  name: yup.string().required(),
  content: yup.string().required(),
  size: yup
    .number()
    .required()
    .max(MAX_SIZE, i18n.t("RESOURCE_FILE_SIZE_ERROR", { size: MAX_SIZE_IN_MB }))
});

type AddResourceFileForm = yup.InferType<typeof AddResourceFileFormSchema>;

export const AddResourceFileModal: React.FC = () => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [isSubmitting, setSubmitting] = useState(false);
  const closeModal = useModalDispatch();
  const [, addResourceFile] = useAddResourceFileMutation();
  const { tenant } = useContext(SelectedTenantContext);
  const { user } = useAuth0();

  const onSubmit = async (values: AddResourceFileForm) => {
    setSubmitting(true);
    const { data, error } = await addResourceFile({
      file: values.content,
      fileName: values.name,
      fileSize: values.size,
      tenantId: tenant?.id,
      userId: user.email
    });

    if (
      error ||
      data?.addResourceFile?.errors?.length ||
      !data?.addResourceFile?.status
    ) {
      const [reason] = data?.addResourceFile?.errors?.[0]?.split(" ") ?? [];
      const message = `${t("ERROR_ADDING_RESOURCE_FILE")}\n${
        reason === "FILE_ALREADY_EXISTS" ? t("FILE_ALREADY_EXISTS") : ""
      }`;

      addNotification({
        type: "error",
        message,
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    } else {
      addNotification({
        type: "success",
        message: t("SUCCESSFULLY_ADDED_RESOURCE_FILE"),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }
    setSubmitting(false);
    closeModal();
  };

  return (
    <ModalForm<AddResourceFileForm>
      name="AddResourceFileModal"
      title={t("ADD_RESOURCE_FILE")}
      validationSchema={AddResourceFileFormSchema}
      initialValues={{
        name: "",
        content: "",
        size: 0
      }}
      onSubmit={onSubmit}
    >
      {({ values, setValues, isValid, dirty, errors }) => (
        <Form>
          <div className="mt-1">
            <FileUploadReader
              onUpload={(response: FileUploadResponse) => {
                setValues({
                  ...values,
                  name: response.name,
                  content: response.content,
                  size: response.size
                });
              }}
              readyText={t("RESOURCE_FILE_UPLOAD_TEXT")}
              draggingText={t("DROP_FILE")}
              errorText={t("ERROR_ADDING_RESOURCE_FILE")}
              returnAs="base64"
            />
            {errors.size && (
              <div className="has-error">
                <span className="error-message">
                  {t("RESOURCE_FILE_SIZE_ERROR", { size: MAX_SIZE_IN_MB })}
                </span>
              </div>
            )}
          </div>
          <div className="btn-container">
            <CancelButton onClick={closeModal} disabled={isSubmitting} />
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!isValid || !dirty}
              labelKey="ADD"
              submittingLabelKey="ADDING"
              testId="add-resource-file"
            />
          </div>
        </Form>
      )}
    </ModalForm>
  );
};
